import { useQuery } from '@tanstack/react-query';
import { makeBackendApi } from 'services/BackendApi';
import { IAnnouncementForPageItem, TAnnouncementAppearIn } from 'services/BackendApi/types/AnnouncementResponse';
import { useMemo } from 'react';
import { parseAsUTC } from 'utils/date.utc';

const formatAsDateParam = (date?: string | null) => {
  if (!date) {
    return null;
  }

  try {
    return parseAsUTC(date);
  } catch {
    return null;
  }
};

export interface IUseFilteredAnnouncementsParams {
  hotelUuid: string | null;
  checkInDate?: string | null; // ISO date string
  checkOutDate?: string | null; // ISO date string
  appearIn: TAnnouncementAppearIn;
  enabled?: boolean;
}

/**
 * Hook to fetch filtered announcements based on user role, TC country, hotel, and dates
 * Filters announcements that match:
 * - Logged in role (INTERNAL/EXTERNAL)
 * - TC country
 * - Hotel UUID
 * - Active from/to dates between hotel check-in/check-out dates
 */
export const useFilteredAnnouncements = (params: IUseFilteredAnnouncementsParams) => {
  const { hotelUuid, checkInDate, checkOutDate, appearIn, enabled = true } = params;

  const backendApi = makeBackendApi();

  return useQuery({
    queryKey: [
      'announcements',
      'filtered',
      hotelUuid,
      checkInDate,
      checkOutDate,
      appearIn,
    ],
    queryFn: async () => {
      if (!hotelUuid) {
        return { data: [], meta: { total: 0 } };
      }

      const queryParams: any = {
        page: appearIn,
        hotelUuid,
      };


      if (checkInDate) {
        queryParams.startDate = parseAsUTC(checkInDate);
      }
      if (checkOutDate) {
        queryParams.endDate = parseAsUTC(checkOutDate);
      }

      const response = await backendApi.getAnnouncementsForPage(queryParams);
      return response.data;
    },
    enabled: enabled && !!hotelUuid,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });
};


